import { UserRole } from '../types';
import type { Project, ServiceContract, ViewState } from '../types';
import type { Traducir } from '../i18n/idioma';
import { nombreDeRol } from '../i18n/nombresDeDatos';
import { getAssignedUserIds } from '../services/projectAdapter';

/**
 * Single source of truth for how each role is presented in the UI.
 * Used by Workforce (Team Directory) and Settings (TeamMemberModal) so the
 * label and badge color stay consistent across the app.
 */
export const ROLE_LABELS: Record<UserRole, string> = {
    Admin: 'Admin',
    Office: 'Office',
    Foreman: 'Foreman',
    Laborer: 'Laborer',
    Maintenance: 'Service Tech',
};

/**
 * Roles allowed to see money: contract revenue, profit, margins, expenses and
 * invoices on a project, and the Finance section.
 *
 * Field roles are deliberately excluded — a Foreman, Laborer or Service Tech
 * must never see what a job was sold for. This lives in one place because the
 * rule was duplicated across ProjectDetail, ProjectsView and FinanceView, and a
 * copy drifting is how pricing leaks to a crew account.
 */
const FINANCIAL_ROLES: UserRole[] = ['Admin', 'Office'];

export function canSeeFinancials(role: UserRole | null | undefined): boolean {
    // An unresolved role must read as "no access", never as a default-allow.
    return !!role && FINANCIAL_ROLES.includes(role);
}

/**
 * Roles allowed to use the AI assistant — the company owner and Admins only.
 *
 * The assistant is handed the whole workspace as context — projects, estimates,
 * contracts, pricebook, business metrics — so access to it is effectively access
 * to every number in the business. Restricting the Financials tab while leaving
 * the chat open to field crew would defeat the point: they could simply ask.
 *
 * WHY 'Admin' ALONE COVERS THE OWNER. There is no 'Owner' role: the company
 * owner is `workspaces.owner_email`, and their role is resolved by the
 * get_my_permissions() RPC as
 *   COALESCE(workspace_users.role, allowed_users.role)
 *
 * OJO: lo que este párrafo decía antes era falso en sus tres afirmaciones. Se
 * corrigió midiendo contra producción el 11 de agosto de 2026. Decía que los
 * dueños «generalmente no tienen fila en workspace_users» —la tienen 163 de
 * 163 resolubles—, que 163 de 165 resuelven a 'Admin' —resuelven todos— y que
 * un dueño carga 'Maintenance' —ninguno—. Importaba porque es lo primero que
 * lee quien toca permisos, y hacía creer que la fila que hoy protege el guard
 * casi nunca existe.
 *
 * Estado real: el dueño tiene fila en workspace_users con role='Admin' en
 * todos los talleres resolubles. El único taller cuyo owner_email no
 * corresponde a ninguna cuenta (diegue377-00976d) no tiene ningún miembro:
 * nació de un alta que falló a la mitad.
 *
 * Y desde agosto de 2026 workspaces.owner_email ya NO es solo informativo: es
 * el ancla del guard _member_guard_violation(), que impide que a un dueño lo
 * quiten o le cambien el rol. Sigue sin haber un rol 'Owner' —meterlo obliga a
 * repasar 66 comparaciones `=== 'Admin'` en 28 archivos, y la que se escape
 * falla en silencio hacia «sin permiso»—. En la interfaz «Owner» es una
 * ETIQUETA derivada del correo, no un rol guardado: ver esDuenoDelTaller() y
 * etiquetaDePuesto() al final de este archivo.
 *
 * 'Office' was removed here deliberately. It is a bookkeeping role, and the
 * assistant is an unrestricted window onto margins, revenue and client
 * contacts.
 */
const AI_ROLES: UserRole[] = ['Admin'];

export function canUseAI(role: UserRole | null | undefined): boolean {
    // An unresolved role must read as "no access", never as a default-allow.
    return !!role && AI_ROLES.includes(role);
}

/**
 * Roles that may see the WHOLE team: the directory (names, emails, roles, crew
 * assignments), everyone's timesheets, and the clock map of where each person
 * punched in and out.
 *
 * Field roles — Foreman, Laborer, Service Tech — get their own clock and
 * nothing else. A crew member has no reason to hold a roster of colleagues'
 * email addresses, read another worker's hours, or see a map of their movements
 * through the day; that last one in particular is location history about a
 * named person.
 *
 * Foreman sits on the restricted side deliberately: crews are built in Service
 * Routes, so leading one never required the directory.
 */
const TEAM_ADMIN_ROLES: UserRole[] = ['Admin', 'Office'];

export function canManageTeam(role: UserRole | null | undefined): boolean {
    // An unresolved role must read as "no access", never as a default-allow.
    return !!role && TEAM_ADMIN_ROLES.includes(role);
}

/**
 * Roles que pueden dar de alta o editar la ficha de un cliente.
 *
 * La regla ya estaba escrita a mano en ClientsView; vive aquí porque dejó de ser
 * cosa de una pantalla: la conversión automática de prospecto a cliente (cuando
 * el cliente firma la propuesta desde el enlace público) crea la ficha SOLA, en
 * cualquier pestaña que tenga la app abierta. Las políticas RLS solo aíslan por
 * taller y no miran el rol, así que sin esta comprobación un Laborer con la app
 * abierta era quien creaba el cliente — una acción que su interfaz ni siquiera
 * le ofrece y que no tiene forma de deshacer.
 */
const CLIENT_ADMIN_ROLES: UserRole[] = ['Admin', 'Office'];

export function canManageClients(role: UserRole | null | undefined): boolean {
    // An unresolved role must read as "no access", never as a default-allow.
    return !!role && CLIENT_ADMIN_ROLES.includes(role);
}

/**
 * Roles that may change a project's SETUP rather than just work on it: the
 * geofence (where the job is and how wide the on-site radius counts).
 *
 * The geofence is the yardstick a crew's own attendance is measured against —
 * whoever can widen the radius or move the pin can make their own off-site
 * clock-ins read as on-site. That has to sit with the office, not with the
 * person being measured, or the audit trail means nothing.
 */
const PROJECT_CONFIG_ROLES: UserRole[] = ['Admin', 'Office'];

export function canConfigureProject(role: UserRole | null | undefined): boolean {
    // An unresolved role must read as "no access", never as a default-allow.
    return !!role && PROJECT_CONFIG_ROLES.includes(role);
}

/**
 * Roles that may open and operate the weekly crew schedule.
 *
 * The board decides which foreman runs which job on which day, and dropping a
 * job on a foreman's lane also writes the project lead. That is planning work
 * the office does, not something a crew member self-serves — a foreman booking
 * himself onto a job would be assigning his own accountability.
 *
 * Crew still see their work: the jobs they are assigned to appear on the
 * project board and in their clock-in picker as they always did.
 */
const SCHEDULE_PLANNER_ROLES: UserRole[] = ['Admin', 'Office'];

export function canPlanSchedule(role: UserRole | null | undefined): boolean {
    // An unresolved role must read as "no access", never as a default-allow.
    return !!role && SCHEDULE_PLANNER_ROLES.includes(role);
}

/**
 * Roles that may name the foreman in charge of a job on a given day.
 *
 * Office is included: the day-to-day lead is now scheduling, and scheduling is
 * the office's job — the same people who operate the weekly board. Restricting
 * it to Admin would have meant the board could assign a day but the project card
 * could not undo it, which is the sort of split that makes people give up and
 * share the admin login.
 *
 * The owner is covered by 'Admin' — see the note on AI_ROLES for why there is no
 * separate Owner role.
 *
 * Field roles are still read-only. Everyone who can open the project SEES who is
 * in charge — a Foreman in particular needs to know it is them — but a foreman
 * must not be able to put himself on, or take himself off, a job.
 */
const LEAD_ASSIGN_ROLES: UserRole[] = ['Admin', 'Office'];

export function canAssignProjectLead(role: UserRole | null | undefined): boolean {
    // An unresolved role must read as "no access", never as a default-allow.
    return !!role && LEAD_ASSIGN_ROLES.includes(role);
}

/**
 * Roles that may open a project's Financials tab to record what the JOB spent:
 * expenses and invoices. Foreman is included — the person on site is the one who
 * knows a load of gravel arrived and what it cost.
 *
 * This is deliberately NARROWER than `canSeeFinancials`. The Analysis sub-tab —
 * contract value, margin, profit — stays office-only, so a Foreman can log what
 * a job COST without ever seeing what it SOLD for. Recording spend and reading
 * the markup are different privileges; conflating them is how pricing leaks to
 * the crew.
 */
const PROJECT_COST_ROLES: UserRole[] = ['Admin', 'Office', 'Foreman'];

export function canLogProjectCosts(role: UserRole | null | undefined): boolean {
    // An unresolved role must read as "no access", never as a default-allow.
    return !!role && PROJECT_COST_ROLES.includes(role);
}

/**
 * Roles that may clock in to ANY active job, not only the ones they are
 * assigned to. Foreman is included: crew leads move between sites, cover for
 * each other and start work before the office has updated an assignment —
 * making them unable to record the hours at all is worse than a slightly loose
 * target list.
 *
 * This is about which jobs appear in the clock-in picker. It is NOT permission
 * to be off-site: the geofence still measures the distance and flags the shift
 * exactly the same, whoever booked it. Nothing here weakens that record.
 *
 * Solo el PEÓN se queda en «únicamente lo asignado». El técnico de servicio SÍ
 * está en la lista de abajo —el comentario decía lo contrario y la prueba de
 * `roles.test.ts` afirma el permiso—, aunque para él sea inocuo en obras:
 * `clockTargetsFor` ya le apaga las obras, así que esta amplitud solo le vale
 * para las rutas.
 */
const ANY_JOB_CLOCK_ROLES: UserRole[] = ['Admin', 'Office', 'Foreman', 'Maintenance'];

export function canClockIntoAnyJob(role: UserRole | null | undefined): boolean {
    // An unresolved role must read as "no access", never as a default-allow.
    return !!role && ANY_JOB_CLOCK_ROLES.includes(role);
}

/**
 * Roles whose entire app is the time clock — one screen, nothing else.
 *
 * A labourer's whole interaction with Raza Pro is punching in and out. Every
 * other surface (jobs, clients, pricing, the assistant, the store) is either
 * information they have no use for or information the business should not be
 * handing to a crew account.
 *
 * This overrides `allowed_views` on purpose: that column is edited by hand when
 * inviting someone and is easy to over-grant by ticking a box. The role itself
 * is the guarantee, so a mistyped invite cannot open up the app.
 */
const CLOCK_ONLY_ROLES: UserRole[] = ['Laborer'];

export function isClockOnlyRole(role: UserRole | null | undefined): boolean {
    return !!role && CLOCK_ONLY_ROLES.includes(role);
}

/**
 * Qué ve cada rol cuando su `allowed_views` viene VACÍA.
 *
 * ── Por qué hace falta ──────────────────────────────────────────────────────
 *
 * `allowed_views` se marca a mano al invitar, y en la base de producción está
 * vacía en 176 de 196 filas (comprobado el 12-ago-2026). Los 165 Admin no
 * importan —`resolvePermissions` les da la lista completa por rol—, pero
 * quedan 3 capataces, 2 de mantenimiento y 2 de oficina con la lista en blanco.
 *
 * Hasta ahora el guardia de rutas se APAGABA con la lista vacía, y por eso esa
 * gente podía trabajar: veían todo. También veían /finance y /materials, que es
 * justo lo que no debían ver. O sea que el agujero y lo que los mantenía vivos
 * eran la misma línea de código.
 *
 * Negar todo al cerrar el agujero habría encerrado a esas 7 personas en el
 * reloj de fichar, con el menú lateral vacío —el propio comentario de
 * ALWAYS_ALLOWED_VIEWS ya advertía de esta trampa—. Derivar del rol cierra el
 * agujero SIN echar a nadie: el capataz deja de alcanzar Finanzas y conserva
 * sus obras.
 *
 * Es un RESPALDO, no la verdad: si la fila trae lista, manda la fila. Lo
 * correcto a medio plazo es llenar esa columna al invitar y borrar esto.
 */
const VISTAS_POR_ROL: Record<UserRole, ViewState[]> = {
    // Nunca se usa —resolvePermissions resuelve Admin antes de llegar aquí—,
    // pero el Record completo obliga a pensar en el rol nuevo que se añada.
    Admin: [],
    // Oficina factura y cotiza: ve dinero (está en FINANCIAL_ROLES).
    Office: [
        'Dashboard', 'Clients', 'Leads', 'Estimates', 'Projects', 'Finance',
        'Calendar', 'FollowUps', 'Services', 'Inventory', 'Workforce',
        'Pricebook', 'TimeClock',
    ],
    // Capataz: manda la obra, no el dinero. Sin Finanzas y sin Precios.
    Foreman: [
        'Projects', 'Calendar', 'Services', 'Clients', 'Inventory', 'TimeClock',
    ],
    // Peón: el reloj. `isClockOnlyRole` ya lo fuerza antes que esto.
    Laborer: ['TimeClock'],
    // Técnico de servicio: sus rutas y su reloj.
    Maintenance: ['Services', 'Calendar', 'Inventory', 'TimeClock'],
};

export function vistasPorDefectoDeRol(role: UserRole | null | undefined): ViewState[] {
    if (!role) return [];
    return VISTAS_POR_ROL[role] ?? [];
}

/** Which KINDS of work a role may book time against. */
export interface ClockTargetKinds {
    /** One-off construction jobs. */
    projects: boolean;
    /** Recurring service-route contracts. */
    routes: boolean;
    /** El taller / la oficina. SIEMPRE `true` — ver `clockTargetsFor`. */
    office: boolean;
}

/**
 * What shows up in the clock-in picker, by role.
 *
 * The two sides of the business are staffed by different people, and offering
 * both to everyone is how hours end up booked against the wrong one:
 *
 *  - **Foreman** runs construction jobs → projects only.
 *  - **Service Tech** runs the recurring routes → routes only.
 *  - **Admin / Office** may need either, e.g. covering a shift.
 *  - **Laborer** keeps both, since a labourer is pulled onto whatever is
 *    running that day. Their list is still limited to their own assignments
 *    (see canClockIntoAnyJob), so it stays short in practice.
 *
 * El TALLER no se cierra a nadie, ni siquiera al rol sin resolver: es la única
 * opción que siempre está, y sin ella un rol podría quedarse sin nada donde
 * fichar. Este comentario ya lo prometía y el código no lo cumplía —el
 * desplegable lo daba por su cuenta, con un valor VACÍO que no escribía ningún
 * destino: 88 de las 98 filas de producción salieron de ahí, y la base no
 * distingue «estuvo en el taller» de «el menú estaba vacío»—. Ahora el taller
 * es un destino con nombre y geocerca, y sale del reparto de tipos como los
 * demás.
 */
export function clockTargetsFor(role: UserRole | null | undefined): ClockTargetKinds {
    switch (role) {
        case 'Admin':
        case 'Office':
            return { projects: true, routes: true, office: true };
        case 'Foreman':
            return { projects: true, routes: false, office: true };
        case 'Maintenance':
            return { projects: false, routes: true, office: true };
        case 'Laborer':
            return { projects: true, routes: true, office: true };
        default:
            // Unresolved role: offer nothing but shop time rather than
            // guessing, matching the default-deny of the helpers above.
            return { projects: false, routes: false, office: true };
    }
}

/**
 * Roles that may MOVE their clock to another target mid-shift, without punching
 * out and back in.
 *
 * Es la jornada real de quien manda en el campo: se ficha en el taller, se
 * carga la troca, se sale a la obra, y si en la obra no hay trabajo se vuelve.
 * Obligar a fichar salida y entrada en cada salto parte el turno en trozos que
 * luego nadie sabe si fueron uno o tres, y la nómina cuenta tres.
 *
 * El PEÓN también, y aquí hubo que corregir el rumbo. Se le negó primero con
 * el argumento de que el traslado era «la puerta de atrás» de la regla de
 * asignación —volver a elegir destino ya estando dentro—, y eso es falso: el
 * panel vuelve a pasar `canClockIntoTarget` en el camino del traslado, así que
 * un peón trasladándose solo puede llegar a una obra SUYA o al taller. No hay
 * puerta que cerrar.
 *
 * Y negárselo rompía el caso que originó todo esto: el peón que ficha en el
 * taller, recoge la troca, se va a la obra y ahí ficha en la obra. Sin
 * traslado, ese peón tiene que fichar salida y entrada — que es exactamente lo
 * que esta ola existe para quitar. Quien limita a un peón es la lista de
 * destinos, no el botón.
 *
 * El TÉCNICO de servicio sí, y no hace falta acotarlo aparte: `clockTargetsFor`
 * ya le apaga las obras, así que su desplegable son sus rutas y el taller, y
 * encadenar dos rutas en un día ES su jornada. Acotarlo «solo a rutas» sale
 * solo del reparto de tipos.
 *
 * O sea: TODOS los roles con identidad resuelta pueden trasladar. Lo que cambia
 * por rol es a DÓNDE, y de eso ya responde `canClockIntoTarget`.
 */
const MID_SHIFT_TRANSFER_ROLES: UserRole[] = ['Admin', 'Office', 'Foreman', 'Maintenance', 'Laborer'];

export function canTransferMidShift(role: UserRole | null | undefined): boolean {
    // An unresolved role must read as "no access", never as a default-allow.
    return !!role && MID_SHIFT_TRANSFER_ROLES.includes(role);
}

/**
 * Un destino concreto del reloj: la obra, la ruta o el taller.
 *
 * La ruta viaja con las cuadrillas de quien pregunta porque un contrato no
 * guarda personas —guarda cuadrillas—, y quién está en cuál lo resuelve el
 * contexto del reloj. Meter aquí esa resolución obligaría a este archivo a
 * conocer `crews`, y entonces la regla de permisos dependería de una consulta.
 */
export type ClockTarget =
    | { kind: 'office' }
    | { kind: 'project'; project: Project }
    | { kind: 'contract'; contract: ServiceContract; myCrewIds: readonly string[] };

/**
 * ¿Este destino está ASIGNADO a esta persona?
 *
 * Es la lista corta que el desplegable enseña por defecto a todo el mundo, y
 * para el peón es además la única. Separada de `canClockIntoTarget` a
 * propósito: al capataz se le OFRECE su lista corta aunque PUEDA fichar en
 * cualquier obra, y confundir «lo mío» con «lo que me dejan» le llenaba el
 * desplegable con las 42 obras del negocio.
 *
 * Sin identidad resuelta responde que NO. `allowedUserId` es la identidad que
 * miran las políticas RLS y tarda un instante en llegar después de entrar; el
 * código anterior daba por bueno ese instante y le enseñaba al peón TODAS las
 * obras activas mientras durara.
 */
export function isAssignedToTarget(
    target: ClockTarget,
    allowedUserId: string | null | undefined,
): boolean {
    // El taller es de todos: nadie «se asigna» al taller.
    if (target.kind === 'office') return true;
    if (!allowedUserId) return false;
    if (target.kind === 'project') {
        // `getAssignedUserIds` y no leer las columnas a mano: la semántica de
        // respaldo (la canónica manda; la heredada solo si la canónica está
        // vacía) decide una AUTORIZACIÓN, y el disparador del servidor la copia
        // exactamente. Dos versiones de esa regla es una obra donde el servidor
        // deja fichar y la pantalla no, o al revés.
        return getAssignedUserIds(target.project).includes(allowedUserId);
    }
    return (target.contract.assignedCrewIds || []).some(id => target.myCrewIds.includes(id));
}

/**
 * ¿Puede esta persona fichar —o mover su reloj— a ESTE destino?
 *
 * Es el único sitio donde se dice la regla de asignación. Antes vivía suelta
 * dentro del panel del reloj, en forma de un `.includes()` metido en un filtro,
 * y por eso no se aplicaba en ningún otro camino: ni al mover el reloj a media
 * jornada, ni cuando el desplegable se quedaba con un destino que ya no lista.
 *
 * Dos comprobaciones, en este orden:
 *   1. el TIPO — el técnico no ficha obras, el capataz no ficha rutas;
 *   2. la ASIGNACIÓN — salvo para quien puede fichar en cualquier obra
 *      (`canClockIntoAnyJob`), que es una decisión de amplitud ya tomada.
 */
export function canClockIntoTarget(
    role: UserRole | null | undefined,
    target: ClockTarget,
    allowedUserId: string | null | undefined,
): boolean {
    const kinds = clockTargetsFor(role);
    if (target.kind === 'office') return kinds.office;
    if (target.kind === 'project' && !kinds.projects) return false;
    if (target.kind === 'contract' && !kinds.routes) return false;
    return canClockIntoAnyJob(role) || isAssignedToTarget(target, allowedUserId);
}

/**
 * Tailwind badge classes per role (background + text) — Sistema 2.0.
 * El rol se lee por NIVEL de acceso (más autoridad = navy más profundo),
 * no por un arcoíris morado/cian/azul/naranja.
 */
export const ROLE_BADGE_CLASSES: Record<UserRole, string> = {
    Admin: 'bg-navy-850 text-white',
    Office: 'bg-navy-100 text-navy-800',
    Foreman: 'bg-navy-50 text-navy-700',
    Laborer: 'bg-acero-100 text-acero-700',
    Maintenance: 'bg-acero-100 text-acero-700',
};

/**
 * «Owner» en la lista de gente es una ETIQUETA, no un rol.
 *
 * El dueño del negocio salía como «Admin», igual que cualquier persona a la
 * que él mismo hubiera invitado, porque el rol guardado es literalmente
 * 'Admin' y no hay ningún otro. Quien es dueño está en
 * `workspaces.owner_email`, y hasta ahora la app entera ignoraba esa columna:
 * `WorkspaceProfile` la tiraba al mapear la fila del taller.
 *
 * Se deriva en vez de guardarse a propósito. Añadir 'Owner' a `UserRole`
 * obliga a repasar 66 comparaciones `=== 'Admin'` repartidas en 28 archivos, y
 * cualquiera que se escape no falla con un error: devuelve `false` y le
 * esconde al dueño un pedazo de su propia app sin decir nada. La etiqueta no
 * mueve ni un permiso.
 *
 * La comparación va en minúsculas y sin espacios por los dos lados: la columna
 * es TEXT, sin FK ni CHECK, y 22 de 165 talleres llegaron a tener el correo
 * del dueño con la inicial en mayúscula.
 */
export const OWNER_LABEL = 'Owner';

export const OWNER_BADGE_CLASSES = 'bg-brand-400 text-navy-950';

const normalizarCorreo = (correo: string | null | undefined): string =>
    (correo || '').trim().toLowerCase();

/** ¿Este correo es el del dueño de este taller? */
export function esDuenoDelTaller(
    correoPersona: string | null | undefined,
    correoDueno: string | null | undefined,
): boolean {
    const persona = normalizarCorreo(correoPersona);
    const dueno = normalizarCorreo(correoDueno);
    // Sin dueño resoluble nadie es dueño — nunca un default-sí.
    return persona !== '' && dueno !== '' && persona === dueno;
}

/** Lo que se imprime en la columna de puesto: «Dueño» gana a «Admin».
 *
 *  Recibe `t` porque ROLE_LABELS y OWNER_LABEL son cadenas fijas en inglés
 *  —«Foreman», «Laborer», «Service Tech»— y salían tal cual en la columna de
 *  puesto con la app en español. Los VALORES no cambian: `UserRole` sigue
 *  siendo el dato que se guarda y con el que se comparan los permisos. */
export function etiquetaDePuesto(
    role: UserRole,
    correoPersona: string | null | undefined,
    correoDueno: string | null | undefined,
    t?: Traducir,
): string {
    if (esDuenoDelTaller(correoPersona, correoDueno)) return t ? t('dato.rol.dueno') : OWNER_LABEL;
    if (t) return nombreDeRol(role, t);
    return ROLE_LABELS[role] || role;
}

/**
 * Traduce el motivo que devuelve el guard de la base de datos a una clave de
 * texto. Sin esto, lo que le llega a la persona es el mensaje crudo de
 * Postgres —«RAZA_OWNER_PROTECTED» o «Forbidden: must be Admin of workspace
 * test-landscaping-2124d3»— metido en un toast rojo.
 *
 * Devuelve null cuando el error no es del guard, para que quien llama pueda
 * caer en su mensaje genérico de siempre en vez de tragarse el error.
 */
export function claveDeErrorDeMembresia(motivo: string | null | undefined): string | null {
    if (!motivo) return null;
    if (motivo.includes('RAZA_OWNER_PROTECTED')) return 'gente.guard.dueno';
    if (motivo.includes('RAZA_PEER_ADMIN_PROTECTED')) return 'gente.guard.otroAdmin';
    if (motivo.includes('RAZA_LAST_ADMIN')) return 'gente.guard.ultimoAdmin';
    return null;
}
